if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]') || this.querySelector('slider-component'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Check if essential elements exist before proceeding
        if (!this.elements.viewer || !this.elements.thumbnails) {
          return;
        }

        this.currentIndex = 0; // Track the current index
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        // Add event listeners for previous and next buttons
        const sliderButtons = this.elements.viewer.querySelectorAll('.slider-button');
        if (sliderButtons.length > 0) {
          sliderButtons.forEach((button, index) => {
            button.addEventListener('click', this.handleArrowClick.bind(this));
          });
        }
        
        const thumbnailButtons = this.elements.thumbnails.querySelectorAll('[data-target]');
        if (thumbnailButtons.length > 0) {
          thumbnailButtons.forEach((mediaToSwitch, index) => {
            const button = mediaToSwitch.querySelector('button');
            if (button) {
              button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target));
            }
          });
        }
        
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail')) {
          this.removeListSemantic();
        }
        
        // Initialize counter on page load
        this.updateCounter();
      }

      handleArrowClick(event) {
        const direction = event.target.closest('.slider-button--next') ? 1 : -1; // Determine direction
        this.changeSlide(direction);
      }

      changeSlide(direction) {
          const visibleSlides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'))
          .filter(slide => 
            slide.style.display !== 'none' &&
            !slide.classList.contains('hide-media') && 
            !slide.classList.contains('product__media-zoom-hover')
          );
        const totalSlides = visibleSlides.length; // Count only visible slides

        if (totalSlides === 0) return; // If no visible slides, exit

        this.currentIndex = (this.currentIndex + direction + totalSlides) % totalSlides; // Wrap around using modulo
        const mediaId = visibleSlides[this.currentIndex].dataset.mediaId; // Get mediaId from the visible slides
        this.setActiveMedia(mediaId); // Change the active media
        
        // Update the counter to show current position
        this.updateCounter();
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId) {
        if (!this.elements.viewer) {
          return;
        }
        
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        // Update current index based on visible slides
        if (this.elements.viewer) {
          const visibleSlides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'))
            .filter(slide => 
              slide.style.display !== 'none' &&
              !slide.classList.contains('hide-media') && 
              !slide.classList.contains('product__media-zoom-hover')
            );
          this.currentIndex = visibleSlides.findIndex(slide => slide.dataset.mediaId === mediaId);
          if (this.currentIndex === -1) this.currentIndex = 0; // Fallback
        }
        
        // Update counter
        this.updateCounter();

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top-60, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      updateCounter() {
        if (!this.elements.viewer) return;
        
        const visibleSlides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'))
          .filter(slide => 
            slide.style.display !== 'none' &&
            !slide.classList.contains('hide-media') && 
            !slide.classList.contains('product__media-zoom-hover')
          );
        
        const counterCurrent = this.querySelector('.slider-counter--current');
        const counterTotal = this.querySelector('.slider-counter--total');
        
        if (counterCurrent && visibleSlides.length > 0) {
          counterCurrent.textContent = this.currentIndex + 1; // +1 because index is 0-based
        }
        
        if (counterTotal) {
          counterTotal.textContent = visibleSlides.length;
        }
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
